/**
 * Wish-heart logic: add/remove from wishlist via iWish API (logged-in users only).
 * - Logged in: window.CUSTOMER.id is set → use iWish API (https://shopapps.in/api-docs/wishlist/V2/).
 * - Not logged in: no wishlist data is stored; heart does not add/remove.
 * - On load: check if product is in wishlist and set CSS class "added".
 * - On click: toggle add/remove and "added" class (logged in only).
 */

const IWISH_API_BASE = 'https://api.myshopapps.com/wishlist/V2';

function isLoggedIn() {
  return !!(window.CUSTOMER && window.CUSTOMER.id);
}

const WISHLIST_BANNER_HIDE_MS = 5000;

/** Show .wishlist-banner with "added to list" text from data-added-to-list-text. Hides again after 5s. */
function showWishlistBannerAdded(isAdded) {
  const banner = document.querySelector('.wishlist-banner');
  if (!banner) return;
  if (banner._hideTimeout) clearTimeout(banner._hideTimeout);
  if (isLoggedIn() && isAdded !== true) {
    banner.querySelector('.wishlist-banner-text-not-logged-in').setAttribute('hidden', '');
    banner.querySelector('.wishlist-banner-text-logged-in').removeAttribute('hidden');
    banner.removeAttribute('hidden');
  } else if(isLoggedIn() == false) {
    banner.querySelector('.wishlist-banner-text-not-logged-in').removeAttribute('hidden');
    banner.querySelector('.wishlist-banner-text-logged-in').setAttribute('hidden', '');
    banner.removeAttribute('hidden');
  }
  banner._hideTimeout = setTimeout(() => {
    banner._hideTimeout = null;
    banner.setAttribute('hidden', '');
  }, WISHLIST_BANNER_HIDE_MS);
}


/** Check if variant is in wishlist (API, logged-in only). Returns Promise<boolean>. */
async function checkInWishlist(variantId, customerId) {
  if (!isLoggedIn()) return false;
  const params = new URLSearchParams({ customer_id: String(customerId), variant_id: String(variantId) });
  const url = `${IWISH_API_BASE}/checkInWishlist?${params}`;
  const headers = {
    xtoken: 'IWISH_API_bea13bf80b1411f1ba1582b78f75bf3d',
    'Content-Type': 'application/x-www-form-urlencoded',
    Accept: 'application/json'
  };
  try {
    const res = await fetch(url, { method: 'GET', headers });
    if (!res.ok) return false;
    const data = await res.json();
    return data.result === true || data.result === 'True';
  } catch (e) {
    console.warn('iwish checkInWishlist failed', e);
    return false;
  }
}

/** Add to wishlist (API, logged-in only). Returns Promise<boolean>. */
async function addToWishlist(heart, customerId) {
  if (!isLoggedIn()) return false;
  const variantId = parseInt(heart.dataset.variantId, 10);
  const productId = parseInt(heart.dataset.productId, 10);
  const url = `${IWISH_API_BASE}/addToWishlist`;
  const options = {
    method: 'POST',
    headers: {
      xtoken: 'IWISH_API_bea13bf80b1411f1ba1582b78f75bf3d',
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json'
    },
    body: new URLSearchParams({
      customer_id: Number(customerId),
      product_id: Number(productId),
      variant_id: Number(variantId),
      category_id: '',
      product_qty: ''
    })
  };
  try {
    const res = await fetch(url, options);
    return res.ok;
  } catch (e) {
    console.warn('iwish addToWishlist failed', e);
    return false;
  }
}

/** Remove from wishlist (API, logged-in only). Returns Promise<boolean>. */
async function removeFromWishlist(heart, customerId) {
  if (!isLoggedIn()) return false;
  const variantId = parseInt(heart.dataset.variantId, 10);
  const options = {
    method: 'DELETE',
    headers: {
      xtoken: 'IWISH_API_bea13bf80b1411f1ba1582b78f75bf3d',
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json'
    }
  };
  const url = `${IWISH_API_BASE}/removeWishlist?customer_id=${Number(customerId)}&variant_id=${Number(variantId)}`;
  try {
    const res = await fetch(url, options);
    return res.ok;
  } catch (e) {
    console.warn('iwish removeWishlist failed', e);
    return false;
  }
}

class WishHeart extends HTMLElement {
  constructor() {
    super();
    this.banner = this.closest('product-card')?.querySelector('.add-to-wish-banner') || null;
    this.init();
  }

  get isPDP() {
    return this.classList.contains('wish-heart_product-info') || !!this.closest('.product-form');
  }

  async init() {
    const variantId = this.dataset.variantId;
    if (!variantId) {
      if (!this.isPDP) this.setupProductCardOrDrawer();
      return;
    }

    const customerId = isLoggedIn() ? window.CUSTOMER?.id : null;
    const inWishlist = await checkInWishlist(variantId, customerId);
    if (inWishlist) this.classList.add('added');

    if (!this.isPDP) {
      this.setupProductCardOrDrawer();
      return;
    }

    this.addEventListener('click', (e) => this.handlePDPClick(e));
  }

  async handlePDPClick(e) {
    e.preventDefault();
    e.stopPropagation();

    console.log('handlePDPClick');


    const variantId = this.dataset.variantId;
    if (!variantId) return;

    const customerId = isLoggedIn() ? window.CUSTOMER.id : null;
    const isAdded = this.classList.contains('added');

    showWishlistBannerAdded(isAdded);

    this.setLoading(true);

    const ok = isAdded
      ? await removeFromWishlist(this, customerId)
      : await addToWishlist(this, customerId);

    this.setLoading(false);

    if (ok) {
      if (isAdded) {
        this.classList.remove('added');
      } else {
        this.classList.add('added');
      }
    }
  }

  setLoading(loading) {
    this.classList.toggle('wish-heart--loading', loading);
    this.style.pointerEvents = loading ? 'none' : '';
  }

  setupProductCardOrDrawer() {
    // Product card / drawer: open drawer on add, or remove from wishlist when already added
    this.addEventListener('click', (e) => this.handleCardOrDrawerClick(e));
  }

  async handleCardOrDrawerClick(e) {
    e.preventDefault();
    e.stopPropagation();
    const customerId = isLoggedIn() ? window.CUSTOMER.id : null;
    const isAdded = this.classList.contains('added');

    if (isAdded) {
      this.setLoading(true);
      const ok = await removeFromWishlist(this, customerId);
      this.setLoading(false);
      if (ok) this.classList.remove('added');
    } else {
      this.setLoading(true);
      const ok = await addToWishlist(this, customerId);
      this.setLoading(false);
      if (ok) this.classList.add('added');
      if (this.banner) this.banner.style.display = 'block';
    }
  }
}

customElements.define('wish-heart', WishHeart);

/**
 * Wishlist page: load product cards into #wishlist-container.
 * Logged-in only: uses iWish API. Not logged in: shows empty state, no data stored.
 * Fetches each product page and extracts the hidden card HTML.
 */

const IWISH_FETCH_WISHLIST_PATH = '/fetchWishlistData';

function fetchProductCardHTML(handle, variantId) {
  const url = `/products/${encodeURIComponent(handle)}${variantId ? `?variant=${variantId}` : ''}`;
  return fetch(url)
    .then((res) => res.text())
    .then((html) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const wrapper = doc.querySelector('[data-product-card]');
      const card = wrapper ? wrapper.querySelector('.card-product') : null;
      if (!card) return '';
      const gridItem = document.createElement('div');
      gridItem.className = 'grid__item';
      gridItem.appendChild(card.cloneNode(true));
      return gridItem.outerHTML;
    })
    .catch((err) => {
      console.error(`[Wishlist] Failed to load card for ${handle}:`, err);
      return '';
    });
}

async function createWishlistCards(result) {
  const container = document.querySelector('#wishlist-container');
  const emptyStateLogged = document.querySelector('.wishlist-page .empty-state-webshop');
  const emptyStateGuest = document.querySelector('.wishlist-page .wishlist-empty-guest');
  const recommendationsWrap = document.querySelector('.wishlist-page .wishlist-recommendations-wrap');
  if (!container) {
    hideWishlistLoading();
    return;
  }

  if (result.length > 0) {
    if (emptyStateLogged) emptyStateLogged.style.display = 'none';
    if (emptyStateGuest) emptyStateGuest.style.display = 'none';
    if (recommendationsWrap) recommendationsWrap.style.display = 'none';
  }

  if (result.length === 0) {
    container.innerHTML = '';
    if (emptyStateLogged) emptyStateLogged.style.display = '';
    if (emptyStateGuest) emptyStateGuest.style.display = '';
    if (recommendationsWrap) recommendationsWrap.style.display = '';
    hideWishlistLoading();
    return;
  }

  const productHandles = result.map((item) => {
    const handle = item.handle || '';
    const variantId = item.variant?.id ?? item.variant_id ?? null;
    return { handle, variantId };
  });

  const htmlParts = await Promise.all(
    productHandles.map(({ handle, variantId }) => fetchProductCardHTML(handle, variantId))
  );
  container.innerHTML = htmlParts.filter(Boolean).join('');
  attachWishlistCardDataAndRemoveButtons(container, result);
  hideWishlistLoading();
  requestAnimationFrame(() => {
    requestAnimationFrame(() => initWishlistCardSliders());
  });
}

/** Hide loading state and show wishlist content. */
function hideWishlistLoading() {
  const page = document.querySelector('.wishlist-page');
  const loading = document.getElementById('wishlist-loading');
  if (page) page.classList.add('is-loaded');
  if (loading) loading.setAttribute('aria-hidden', 'true');
}

/** Attach variant id to each card and add remove (X) button; bind remove action. */
function attachWishlistCardDataAndRemoveButtons(container, result) {
  const items = container.querySelectorAll('.grid__item');
  const customerId = isLoggedIn() ? window.CUSTOMER?.id : null;

  items.forEach((gridItem, i) => {
    const item = result[i];
    if (!item) return;
    const variantId = item.variant?.id ?? item.variant_id;
    if (variantId != null) {
      gridItem.dataset.variantId = String(variantId);
    }

    const card = gridItem.querySelector('.card-product');
    if (!card) return;
    gridItem.style.position = 'relative';
    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'wishlist-card-remove';
    removeBtn.setAttribute('aria-label', 'Remove from wishlist');
    removeBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
<mask id="mask0_4392_552" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="18" height="18">
<rect width="17.6533" height="17.6533" fill="#D9D9D9"/>
</mask>
<g mask="url(#mask0_4392_552)">
<path d="M4.61383 13.6276L4.02539 13.0391L8.23791 8.82659L4.02539 4.61408L4.61383 4.02563L8.82635 8.23815L13.0389 4.02563L13.6273 4.61408L9.41479 8.82659L13.6273 13.0391L13.0389 13.6276L8.82635 9.41504L4.61383 13.6276Z" fill="black"/>
</g>
</svg>`;
    removeBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      if (!gridItem.dataset.variantId) return;
      removeBtn.disabled = true;
      const fakeHeart = { dataset: { variantId: gridItem.dataset.variantId } };
      const ok = await removeFromWishlist(fakeHeart, customerId);
      if (ok) {
        gridItem.remove();
        const remaining = container.querySelectorAll('.grid__item').length;
        updateHeaderWishlistCount(remaining);
        if (remaining === 0) {
          const emptyStateLogged = document.querySelector('.wishlist-page .empty-state-webshop');
          const emptyStateGuest = document.querySelector('.wishlist-page .wishlist-empty-guest');
          const recommendationsWrap = document.querySelector('.wishlist-page .wishlist-recommendations-wrap');
          if (emptyStateLogged) emptyStateLogged.style.display = '';
          if (emptyStateGuest) emptyStateGuest.style.display = '';
          if (recommendationsWrap) recommendationsWrap.style.display = '';
        }
      } else {
        removeBtn.disabled = false;
      }
    });
    gridItem.insertBefore(removeBtn, gridItem.firstChild);
  });
}

/** Initialize Flickity sliders on product card carousels (images) inside the wishlist. */
function initWishlistCardSliders() {
  if (typeof Flickity === 'undefined') return;
  const container = document.querySelector('#wishlist-container');
  if (!container) return;
  const carousels = container.querySelectorAll('.product-card-carousel');
  carousels.forEach((el) => {
    if (el.classList.contains('flickity-enabled')) return;
    const flkty = new Flickity(el, {
      cellAlign: 'center',
      contain: true,
      wrapAround: true,
      initialIndex: 0,
      imagesLoaded: true,
      prevNextButtons: true,
      pageDots: true,
      lazyLoad: true
    });
    el.querySelectorAll('.flickity-button').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
      });
    });
  });
}

function updateHeaderWishlistCount(count) {
  const el = document.querySelector('.header__nav-icon-wish-span') || document.querySelector('[data-wishlist-count]');
  if (!el) return;
  if (count > 0) {
    el.removeAttribute('hidden');
    el.textContent = count;
  } else {
    el.setAttribute('hidden', '');
  }
}

async function loadWishlistPage() {
  const container = document.querySelector('#wishlist-container');
  if (!container) {
    hideWishlistLoading();
    return;
  }

  if (isLoggedIn()) {
    const customerId = window.CUSTOMER?.id;
    if (!customerId) {
      hideWishlistLoading();
      return;
    }
    const url = `${IWISH_API_BASE}${IWISH_FETCH_WISHLIST_PATH}/${customerId}/`;
    const options = {
      method: 'GET',
      headers: {
        xtoken: 'IWISH_API_bea13bf80b1411f1ba1582b78f75bf3d',
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json'
      }
    };
    try {
      const response = await fetch(url, options);
      const data = await response.json();
      const activeItems = (data.result || []).filter((item) => (item.tags || []).indexOf('hidden') === -1);
      await createWishlistCards(activeItems);
      updateHeaderWishlistCount(activeItems.length);
    } catch (e) {
      console.error('[Wishlist] Failed to fetch wishlist:', e);
      hideWishlistLoading();
    }
    return;
  }

  await createWishlistCards([]);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', loadWishlistPage);
} else {
  loadWishlistPage();
}
